import type { AppService } from '@inform-appshell/fusion-kit-contracts';
import type { i18n } from 'i18next';

/**
 * I18nService wraps the i18next instance for sharing with MFEs via ServiceProvider.
 *
 * MFEs can:
 * - Get the shared i18n instance to ensure synchronized language switching
 * - Add their own translation namespaces via addResourceBundle()
 */
export class I18nService implements AppService {
  static readonly SERVICE_ID = 'I18nService';
  readonly id = I18nService.SERVICE_ID;

  constructor(private readonly i18nInstance: i18n) {}

  getI18n(): i18n {
    return this.i18nInstance;
  }

  addResourceBundle(lng: string, ns: string, resources: Record<string, unknown>, deep = true, overwrite = false): void {
    this.i18nInstance.addResourceBundle(lng, ns, resources, deep, overwrite);
  }

  get language(): string {
    return this.i18nInstance.language;
  }

  changeLanguage(lng: string): Promise<void> {
    return this.i18nInstance.changeLanguage(lng).then(() => undefined);
  }

  /**
   * Subscribe to language changes. Returns unsubscribe function.
   * Compatible with useSyncExternalStore.
   */
  onLanguageChanged(callback: (lng: string) => void): () => void {
    this.i18nInstance.on('languageChanged', callback);
    return () => {
      this.i18nInstance.off('languageChanged', callback);
    };
  }
}
